// @flow
import { useQuery } from 'react-query';
import type { QueryOptions, UseQueryResult } from 'react-query';
import AffordableService from '../../services/affordableService';
import useAbortController from '../useAbortController';
import { toFiniteNumber } from '../../utils/numbers';

type ProgramIncomeLevel = {
  programName: string,
  eligible: ?boolean,
  incomeLevel: ?string,
};

type IncomeLevelByProgram = {
  incomeLevel: string,
  programs: Array<ProgramIncomeLevel>,
};

type Params = {
  organizationId: ?string,
  propertyId: ?string,
  anticipatedAnnualIncome: ?string,
  householdSize: ?number,
  options?: QueryOptions<?IncomeLevelByProgram, mixed, ?IncomeLevelByProgram>,
};

/**
 * The household's income level for each affordable program the property
 * carries, scored against the property's current income limits.
 *
 * A useQuery over a POST, against the verb split in AGENTS.md: the route is a
 * POST only because its payload is too large for a query string, and this is a
 * read that has to re-run whenever its inputs change. useMutation would leave
 * the caller hand-rolling that refetch.
 */
export default function useIncomeLevelByProgramQuery({
  organizationId,
  propertyId,
  anticipatedAnnualIncome,
  householdSize,
  options = {},
}: Params): UseQueryResult<?IncomeLevelByProgram, mixed> {
  const abortControllerOptions = useAbortController();

  const queryKey = [
    'useIncomeLevelByProgramQuery',
    organizationId,
    propertyId,
    anticipatedAnnualIncome,
    householdSize,
  ];

  const enabled =
    !!organizationId &&
    !!propertyId &&
    toFiniteNumber(anticipatedAnnualIncome) !== undefined &&
    (toFiniteNumber(householdSize) ?? 0) > 0 &&
    options.enabled !== false;

  return useQuery(
    queryKey,
    async () => {
      if (!organizationId || !propertyId) {
        return null;
      }

      const affordableService = new AffordableService();
      // The payload schema types the income as a string; the backend coerces it.
      const response = await affordableService.calculateIncomeLevel(
        organizationId,
        propertyId,
        { anticipatedAnnualIncome, householdSize },
        abortControllerOptions,
      );

      return response?.data ?? null;
    },
    {
      ...options,
      enabled,
      refetchOnWindowFocus: false,
      onError: (error) => {
        if (options.onError) {
          options.onError(error);
        }
      },
    },
  );
}
